import { Plus, X } from 'lucide-react';
import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ContactHandlesList } from '@/components/ui/ContactHandlesList';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BenefitType } from '@/services/types/coverage-types';

import { CoverageFormData } from '../types';

interface AdjustersSectionProps {
  register: UseFormRegister<CoverageFormData>;
  setValue: UseFormSetValue<CoverageFormData>;
  watch: UseFormWatch<CoverageFormData>;
  adjusterFields: FieldArrayWithId<CoverageFormData, 'adjusters', 'id'>[];
  appendAdjuster: UseFieldArrayAppend<CoverageFormData, 'adjusters'>;
  removeAdjuster: UseFieldArrayRemove;
}

export const AdjustersSection = ({
  register,
  setValue,
  watch,
  adjusterFields,
  appendAdjuster,
  removeAdjuster,
}: AdjustersSectionProps) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Adjusters</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              appendAdjuster({
                benefit_type: null,
                name: null,
                handles: null,
              })
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Adjuster
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {adjusterFields.map((field, index) => (
          <div key={field.id} className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Adjuster {index + 1}</h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeAdjuster(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor={`adjusters.${index}.name`}>Adjuster Name</Label>
                <Input
                  id={`adjusters.${index}.name`}
                  placeholder="e.g., John Smith"
                  {...register(`adjusters.${index}.name`)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`adjusters.${index}.benefit_type`}>
                  Assigned to Benefit
                </Label>
                <Select
                  value={watch(`adjusters.${index}.benefit_type`) || undefined}
                  onValueChange={(value) =>
                    setValue(
                      `adjusters.${index}.benefit_type`,
                      value ? (value as BenefitType) : null,
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select benefit type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={BenefitType.PERSONAL_INJURY_PROTECTION}>
                      Personal Injury Protection (PIP)
                    </SelectItem>
                    <SelectItem value={BenefitType.MEDICAL_PAYMENTS}>
                      Medical Payments
                    </SelectItem>
                    <SelectItem value={BenefitType.UNINSURED_MOTORIST}>
                      Uninsured Motorist
                    </SelectItem>
                    <SelectItem value={BenefitType.UNDER_INSURED_MOTORIST}>
                      Underinsured Motorist
                    </SelectItem>
                    <SelectItem value={BenefitType.BODILY_INJURY_LIABILITY}>
                      Bodily Injury Liability
                    </SelectItem>
                    <SelectItem value={BenefitType.PROPERTY_DAMAGE_LIABILITY}>
                      Property Damage Liability
                    </SelectItem>
                    <SelectItem value={BenefitType.COMPREHENSIVE_AUTO}>
                      Comprehensive Auto
                    </SelectItem>
                    <SelectItem value={BenefitType.COLLISION_AUTO}>
                      Collision Auto
                    </SelectItem>
                    <SelectItem value={BenefitType.WORKERS_COMPENSATION}>
                      Workers Compensation
                    </SelectItem>
                    <SelectItem value={BenefitType.OTHER}>Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Adjuster Contact Information</Label>
                <ContactHandlesList
                  handles={watch(`adjusters.${index}.handles`) || []}
                  onUpdate={async (handles) =>
                    setValue(
                      `adjusters.${index}.handles`,
                      handles.length > 0 ? handles : null,
                    )
                  }
                  title=""
                  emptyStateTitle="No adjuster contacts"
                  emptyStateDescription="Add contact information for this adjuster."
                />
              </div>
            </div>
          </div>
        ))}

        {adjusterFields.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            <p>No adjusters added yet.</p>
            <p className="text-sm">
              Click &quot;Add Adjuster&quot; to start adding adjusters.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
